import Vue from 'vue';

const __eventQueues = {};

/**
 * Methods queue, listenToQueue and removeQueueListener is used exactly the same way as Vue methods
 * $emit, $on and $off.
 *
 * The difference between these events, employing EventQueue, and normal Vue events is that
 * EventQueue events queue up if there is no listener available.
 * They are emited first when there is at least one event listener available.
 */

export default new Vue({
	data() {
		return {
			eventQueues: __eventQueues,
		};
	},

	methods: {
		queue(eventName, eventData) {
			this.eventQueues[eventName] = new EventQueue(eventName, { eventData });
		},

		listenToQueue(eventName, listener) {
			this.eventQueues[eventName] = new EventQueue(eventName, { listener });
		},

		removeQueueListener(eventName, listener) {
			if (!this.eventQueues[eventName])
				throw new Error(`Cannot remove listener from queue ${eventName}. This queue does not exist`);
			this.eventQueues[eventName].removeListener(listener);
		}
	}
});

class EventQueue {
	constructor(eventName, { listener, eventData }) {
		if (!eventName) throw new Error('Cannot construct EventQueue: Missing eventName.');
		if (__eventQueues[eventName]) {
			if (listener) __eventQueues[eventName].registerListener(listener);
			if (eventData) __eventQueues[eventName].queueEvent(eventData);
			return __eventQueues[eventName];
		}
		this.listeners = listener ? [listener] : [];
		this.isReady = Boolean(listener);
		this.queue = eventData ? [eventData] : [];
		return this;
	}

	queueEvent(eventData) {
		if (this.isReady) {
			this.emitEventToAllListeners(eventData);
		} else {
			this.queue.push(eventData);
		}
	}

	emitAllEvents() {
		if (!this.listeners.length) throw new Error('Cannot emit events from EventQueue: Missing listener.');
		for (let i = 0; i < this.queue.length; i++) {
			this.emitEventToAllListeners(this.queue[i]);
			this.queue.splice(i, 1);
		}
	}

	emitEventToAllListeners(eventData) {
		this.listeners.forEach(listener => {
			listener(eventData);
		});
	}

	registerListener(listener) {
		this.listeners.push(listener);
		if (!this.isReady) this.emitAllEvents();
		this.isReady = true;
	}

	removeListener(listener) {
		let index = this.listeners.indexOf(listener);
		if (index < 0) {
			throw new Error({
				message: 'Cannot remove listener from EventQueue, listener not found in listeners array.',
				data: {
					listener,
					listeners: this.listeners,
				},
			});
		}
		this.listeners.splice(index, 1);
		if (!this.listeners.length) this.isReady = false;
	}
}
